// src/features/permissions/components/PermissionListCard.tsx
import { Card } from '../../../app/components/foundation';
import type { SecurityRole } from '../../security/types/security.types';

export interface PermissionListCardProps {
  permissions: SecurityRole['permission_details'];
}

function getPermissionPresentation(permission: SecurityRole['permission_details'][number]) {
  const isSystem = permission.scope === 'system';

  if (permission.key === 'tenant.manage') {
    return {
      label: isSystem ? 'Manage Tenants (Platform Level)' : 'Manage Current Tenant',
      explanation: isSystem
        ? 'Applies to all tenants in VaniaLabs platform'
        : 'Applies only to this tenant',
    };
  }

  return {
    label: permission.name,
    explanation: isSystem
      ? 'Applies to all tenants in VaniaLabs platform'
      : 'Applies only to this tenant',
  };
}

/**
 * Workspace card for the structured RBAC permission contract. Scope is
 * rendered from the API for every row so identical keys remain distinct.
 */
export function PermissionListCard({ permissions }: PermissionListCardProps) {
  return (
    <Card className="vania-permissions-workspace-card">
      <h2 className="vania-permissions-workspace-card-title">مجوزهای دسترسی</h2>

      {permissions.length === 0 ? (
        <p className="vania-permissions-workspace-empty">مجوزی برای این نقش ثبت نشده است.</p>
      ) : (
        <ul className="vania-permissions-workspace-list">
          {permissions.map((permission) => {
            const presentation = getPermissionPresentation(permission);
            const isSystem = permission.scope === 'system';

            return (
              <li
                key={`${permission.scope}:${permission.id}`}
                className={`vania-permissions-workspace-list-item ${isSystem ? 'is-system' : 'is-tenant'}`}
                title={presentation.explanation}
              >
                <span className="vania-permissions-workspace-list-primary" dir="ltr">
                  {permission.key}
                </span>
                <span className={`vania-permission-scope-badge ${isSystem ? 'is-system' : 'is-tenant'}`}>
                  {isSystem ? 'SYSTEM' : 'TENANT'}
                </span>
                <span className="vania-permissions-workspace-list-secondary" dir="ltr">
                  Source: {permission.source ?? (permission.scope === 'system' ? 'system_role_permissions' : 'role_permissions')}
                </span>
                <span className="vania-permissions-workspace-list-secondary">
                  {presentation.label}
                </span>
                <span className="vania-permissions-workspace-list-secondary vania-permissions-scope-explanation">
                  {presentation.explanation}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
